export const vserver = {
  title: 'VServer',
  subtitle: 'Manage your virtual servers',
  noSubscription: {
    title: 'No VServer yet',
    description: 'Order your first VServer in a few clicks.',
    action: 'Order VServer'
  },
  selector: {
    none: 'No VServer selected',
    label: 'Active VServer'
  },
  tabs: {
    overview: 'Overview',
    console: 'Console',
    network: 'Network',
    snapshots: 'Snapshots',
    graphs: 'Metrics',
    settings: 'Settings'
  },
  status: {
    provisioning: 'Provisioning',
    active: 'Active',
    stopped: 'Stopped',
    failed: 'Failed',
    resizing: 'Resizing',
    reinstalling: 'Reinstalling',
    suspended: 'Ended',
    deprovisioning: 'Deleting',
    deprovisioned: 'Deleted'
  },
  overview: {
    sectionTitle: 'Server information',
    hostname: 'Hostname',
    status: 'Status',
    ipv4: 'IPv4 address',
    ipv6: 'IPv6 prefix',
    mac: 'MAC address',
    os: 'Operating system',
    plan: 'Plan',
    vcpu: 'vCPU',
    memory: 'Memory',
    disk: 'Disk',
    uptime: 'Uptime',
    guestAgent: 'Guest agent',
    guestAgentRunning: 'Active',
    guestAgentStopped: 'Not detected',
    guestAgentHint: 'The guest agent is not responding. Features like password reset and SSH key injection require a running agent.',
    keylessTitle: 'No access set up yet',
    keylessMessage: 'Your server is running but has no SSH key yet. Inject a key now — or set a root password for the web console under settings.',
    keylessAction: 'Inject SSH key',
    failedTitle: 'Operation failed',
    failedProvisioning: 'Setting up your server failed. We retry automatically. If that does not work, we refund the amount and send you an email.',
    failedOperation: 'The last operation on your server failed. Contact support if the server does not come back up.',
    suspendedTitle: 'Subscription ended, server stopped',
    suspendedBody: 'This server\'s subscription has ended, so it was stopped. It is kept with its disk, snapshots and IP address until the end of {date} and deleted after that. To keep it, contact our support before then.',
    suspendedBodyUndated: 'This server\'s subscription has ended, so it was stopped. It is kept with its disk, snapshots and IP address for 30 days and deleted after that. To keep it, contact our support before then.',
    createdAt: 'Created',
    provisionedAt: 'Provisioned',
    powerControls: 'Power controls',
    start: 'Start',
    stop: 'Stop',
    restart: 'Restart',
    confirmStop: 'Really stop this VServer?',
    confirmRestart: 'Really restart this VServer?'
  },
  console: {
    title: 'Web console',
    subtitle: 'Connect to your VServer through the web console.',
    connect: 'Connect',
    disconnect: 'Disconnect',
    fullscreen: 'Fullscreen',
    sendCtrlAltDel: 'Send Ctrl+Alt+Del',
    pasteClipboard: 'Paste from clipboard',
    connecting: 'Connecting...',
    connected: 'Connected',
    disconnected: 'Disconnected',
    notRunning: 'The VServer must be running to open the console.',
    locked: 'The console is unavailable while the server is locked.',
    error: 'Console connection failed'
  },
  identityUnproven: {
    title: 'Controls blocked',
    body: 'We could not confirm that the virtual machine behind this server belongs to it. To protect your data, starting, stopping, the console and the other actions are blocked until our support has resolved this. Please contact support.'
  },
  locked: {
    title: 'Server locked',
    body: 'We have locked and stopped this server. You cannot start it, reinstall it or restore it to a snapshot. Its disk, snapshots and IP addresses are kept, and you can still delete it.',
    note: 'Reason: {note}',
    contact: 'If you have fixed the breach or want to object, contact our support.'
  },
  paymentDue: {
    title: 'Payment for your tier switch is open',
    body: 'The payment for switching your server to the higher tier failed. The server already runs on the new tier. Pay the difference of {amount} by {date} or switch the server back to its previous tier. If you do neither, the contract ends when that period expires.',
    bodyUnpriced: 'The payment for switching your server to the higher tier failed. The server already runs on the new tier. Pay the difference by {date} or switch the server back to its previous tier. If you do neither, the contract ends when that period expires.',
    pay: 'Pay the difference',
    switchBack: 'Switch back to the previous tier'
  },
  smtp: {
    title: 'Outbound port 25',
    submissionHint: 'Mail to your mail provider over the submission ports 587 and 465 works at any time.',
    opensAt: 'The block ends automatically on {date}.',
    pendingShort: 'We are reviewing your request and will reply by email.',
    status: {
      open: 'Outbound port 25 (delivery to other mail servers) is open for your VServers.',
      blocked: 'For the first three months after your first VServer order, outbound port 25 (delivery to other mail servers) is blocked for all your VServers to keep spam off fresh servers.',
      pending: 'You requested the unblock on {date}. We are reviewing it and will reply by email.',
      denied: 'Your request was declined. This does not change the automatic unblock.',
      deniedReason: 'Reason: {reason}',
      cooldown: 'You can send a new request from {date}.',
      forced: 'We have blocked outbound port 25 for all your VServers because there are indications of spam being sent from them. We have sent you the reasons by email. If you want to object, contact our support.'
    },
    request: {
      button: 'Request unblock',
      dialogTitle: 'Unblock port 25 early',
      dialogDescription: 'Briefly describe what you need port 25 for, such as your own mail server for your domains. We review every request by hand and reply by email.',
      reasonLabel: 'Intended use',
      reasonPlaceholder: 'e.g. my own mail server for my domain',
      submit: 'Send request',
      success: 'Request sent',
      error: 'Request not sent'
    },
    errors: {
      notBlocked: 'Port 25 is already open for your VServers.',
      noVServer: 'You can request the unblock once you have a VServer.',
      forced: 'We have blocked port 25 for your VServers. You cannot request an unblock here, please contact our support.',
      cooldown: 'After a declined request you cannot send a new one for 14 days.',
      rateLimited: 'You have already sent several requests today. Please try again tomorrow.'
    }
  },
  network: {
    ipv4Title: 'IPv4',
    ipv6Title: 'IPv6 /112',
    ptrTitle: 'PTR record',
    ptrLabel: 'Reverse DNS',
    ptrSave: 'Save PTR',
    ptrSaved: 'PTR record updated',
    ptrUnavailable: 'Support sets the PTR record for you. Send us the name your address should resolve to.',
    firewallTitle: 'Firewall rules',
    firewallAdd: 'Add rule',
    firewallEmpty: 'No firewall rules yet.',
    firewallDeleteConfirm: 'Delete firewall rule #{position}?',
    table: {
      position: '#',
      direction: 'Direction',
      action: 'Action',
      protocol: 'Protocol',
      source: 'Source',
      dest: 'Destination',
      ports: 'Ports',
      comment: 'Comment',
      enabled: 'Enabled'
    }
  },
  snapshots: {
    title: 'Snapshots',
    subtitle: 'Restore your server to an earlier point at any time.',
    create: 'Create snapshot',
    name: 'Name',
    description: 'Description',
    size: 'Size',
    status: 'Status',
    createdAt: 'Created',
    rollback: 'Restore to this point',
    delete: 'Delete',
    autoBadge: 'Automatic',
    empty: 'No snapshots yet.',
    emptyHint: 'Create a snapshot so you can restore to this point at any time.',
    confirmRollback: 'Roll back to snapshot {name}? All changes since the snapshot will be lost.',
    confirmDelete: 'Delete snapshot {name}?',
    usage: '{used} of {limit} in use',
    limitHint: 'Your plan includes {limit} snapshots. Delete one to take a new one.',
    createSuccess: 'Snapshot {name} created.',
    rollbackSuccess: 'Snapshot restored.',
    deleteSuccess: 'Snapshot deleted.'
  },
  graphs: {
    title: 'Metrics',
    cpu: 'CPU',
    memory: 'Memory',
    disk: 'Disk I/O',
    network: 'Network',
    timeframe: {
      hour: 'Hour',
      day: 'Day',
      week: 'Week',
      month: 'Month'
    },
    noData: 'No metrics available.'
  },
  settings: {
    title: 'Settings',
    hostnameTitle: 'Hostname',
    hostnameLabel: 'New hostname',
    hostnameSave: 'Save hostname',
    hostnameSaved: 'Hostname updated.',
    hostnameInvalid: 'Letters, digits, hyphens and dots only, at most 63 characters.',
    reinstallTitle: 'Reinstall',
    reinstallDescription: 'Wipes the VServer and reprovisions from a fresh template. All data will be lost.',
    reinstallAction: 'Reinstall OS',
    resizeTitle: 'Change plan',
    resizeDescription: 'Switch to a larger or smaller plan (requires a reboot).',
    resizeAction: 'Change plan',
    resetPasswordTitle: 'Reset root password',
    resetPasswordDescription: 'Sets a new root password via the guest agent. The password is shown once.',
    resetPasswordAction: 'Reset password',
    sshKeyTitle: 'Install SSH key',
    sshKeyDescription: 'Installs one of your saved SSH keys onto the running server via the guest agent.',
    sshKeyAction: 'Install SSH key',
    rescueTitle: 'Rescue system',
    rescueDescription: 'Boots the VServer into a live environment. Boot order resets after the next start.',
    rescueAction: 'Start rescue mode',
    exitRescueAction: 'Exit rescue mode',
    deleteTitle: 'Delete VServer',
    deleteDescription: 'Permanently deletes the VServer. This action cannot be undone.',
    deleteAction: 'Delete VServer'
  },
  dialogs: {
    reinstall: {
      title: 'Reinstall operating system',
      message: 'All data on the VServer will be erased. Continue?',
      templateLabel: 'OS template',
      sshKeysLabel: 'SSH public keys (one per line, optional)',
      confirm: 'Reinstall',
      cancel: 'Cancel',
      typeHostnameHint: 'Type {hostname} to confirm',
      invalidKey: 'At least one SSH key is invalid. The server was left unchanged.'
    },
    resize: {
      title: 'Change plan',
      message: 'Pick a new plan for this VServer.',
      diskGrowOnly: 'Note: disks can only grow, not shrink.',
      resizeDiskLabel: 'Grow disk to new plan size',
      keepDiskHint: 'Keeps the current disk size. This is required if you might want to downgrade to a smaller plan later — a grown disk can no longer be shrunk.',
      skipSnapshotLabel: 'Skip safety snapshot',
      safetySnapshotHint: 'A snapshot is taken automatically before the change so you can roll back if something goes wrong.',
      skipSnapshotHint: 'No automatic snapshot is taken. Rolling back will not be possible.',
      confirm: 'Apply plan',
      cancel: 'Cancel',
      // A plan paid for 3, 6 or 12 months: the server is resized before the
      // payment arrives (vServer agreement § 7.5).
      termCharge: 'For the time from today until {date} we now charge the difference of {amount} to your saved payment method. We resize the server at once, even if the payment, for example by SEPA direct debit, has not arrived yet; the new tier is billed from when the payment arrives. If it fails, the server stays resized, and within 14 days you can pay the difference or switch the server back to its previous tier. If you do neither, the contract ends when that period expires.',
      termPending: 'Your SEPA direct debit is still being collected. We resize your server now; the new tier is billed from when the payment arrives. If the debit fails, the server stays resized, and within 14 days you can pay the difference or switch the server back to its previous tier.',
      // Paying a difference already due: the server runs on that tier, and
      // the first due date stands.
      payCharge: 'Your server already runs on this tier and is not resized. For the time from today until {date} we now charge the difference of {amount} to your saved payment method; the new tier is billed from when the payment arrives. If the payment fails, the contract ends when the payment period expires on {deadline} or, if that is later, when the payment fails.',
      payPending: 'Your SEPA direct debit is still being collected. Your server keeps running on this tier, which is billed from when the payment arrives. If the debit fails, the contract ends when the payment period expires on {deadline} or, if that is later, when the debit fails.',
      afterPaymentNote: 'Because a payment for a tier switch of a contract in your customer account failed within the last twelve months, we resize the server only once the payment has arrived.',
      resizeBack: 'We switch the server back to the tier you paid for. Nothing is then left to pay, and nothing is charged.'
    },
    delete: {
      title: 'Delete VServer',
      message: 'This permanently deletes the VServer including all snapshots and IP allocations.',
      typeHostname: 'Type {hostname} to confirm.',
      confirm: 'Delete permanently',
      cancel: 'Cancel'
    },
    snapshot: {
      title: 'Create snapshot',
      nameLabel: 'Name',
      descriptionLabel: 'Description (optional)',
      vmstateLabel: 'Include memory (VMState)',
      confirm: 'Create',
      cancel: 'Cancel'
    },
    firewall: {
      titleCreate: 'Add firewall rule',
      titleEdit: 'Edit firewall rule',
      directionLabel: 'Direction',
      actionLabel: 'Action',
      protocolLabel: 'Protocol',
      sportLabel: 'Source port',
      dportLabel: 'Destination port',
      sourceLabel: 'Source (IP/CIDR)',
      destLabel: 'Destination (IP/CIDR)',
      commentLabel: 'Comment',
      enableLabel: 'Rule enabled',
      save: 'Save',
      cancel: 'Cancel',
      invalidRule: 'The rule was refused. Check the fields and try again.',
      limitReached: 'This server already holds {limit} firewall rules, the most it can have. Delete a rule before you add another.',
      busy: 'Another firewall change is in progress on this server. Try again in a moment.',
      rateLimited: 'Too many firewall changes in a short time. Wait a minute, then try again.',
      changed: 'The firewall rules changed since this list was loaded. The current rules are shown now, check them and try again.',
      invalidStored: 'The firewall cannot be switched on while rules {positions} cannot be applied. Change or delete them, then try again.',
      notReady: 'Firewall rules can only be changed while the server is running or stopped, not while it is being set up, reinstalled or moved to another plan.',
      ruleExpansion: 'A rule may match at most {limit} pairs of source and destination address (the number of sources times the number of destinations).',
      totalExpansion: 'The rules of this server may match at most {limit} pairs of source and destination address together. Shorten the address lists of some rules first.'
    },
    rescue: {
      title: 'Start rescue mode',
      message: 'The VServer reboots into the rescue system. Open the console afterwards to use it.',
      confirm: 'Start rescue',
      cancel: 'Cancel'
    },
    exitRescue: {
      title: 'Exit rescue mode',
      message: 'The VServer will reboot and boot from its normal disk again.',
      confirm: 'Exit rescue',
      cancel: 'Cancel',
      success: 'Exiting rescue mode.'
    },
    resetPassword: {
      title: 'Reset root password',
      message: 'A new root password is set via the guest agent and shown once. The server must be running.',
      confirm: 'Reset password',
      cancel: 'Cancel',
      close: 'Close',
      password: 'New root password',
      shownOnce: 'Write down the password now — it is shown only once.'
    },
    sshKey: {
      title: 'Install SSH key',
      message: 'Install one of your saved SSH keys onto the running server via the guest agent.',
      keyLabel: 'Saved SSH key',
      replaceLabel: 'Replace all existing keys',
      appendHint: 'The key is appended to the existing authorized_keys.',
      replaceHint: 'All existing authorized_keys are replaced with this key.',
      noKeys: 'You have no saved SSH keys yet.',
      manageKeys: 'Manage SSH keys',
      confirm: 'Install',
      cancel: 'Cancel',
      success: 'SSH key installed.'
    }
  },
  errors: {
    serviceEnded: 'This server\'s subscription has ended, so it can no longer be started or changed.',
    locked: 'We have locked this server. You cannot start or change it, only delete it.',
    snapshotBusy: 'Another snapshot action is still running on this server. Try again once it has finished.',
    busy: 'The server\'s configuration is busy. Try again in a minute.',
    identityUnprovenDelete: 'Billing for this server has ended. We will delete the virtual machine once our support has confirmed it belongs to this server.',
    identityUnproven: 'We could not confirm that the virtual machine behind this server belongs to it, so the action was not carried out, to protect your data. Please contact support.',
    fetchFailed: 'Failed to load VServers.',
    actionFailed: 'Action failed.',
    consoleUnavailable: 'Console is unavailable.',
    statsUnavailable: 'Metrics are unavailable.'
  },
  shop: {
    productMissing: 'Product not yet available in the catalog.',
    sshKeyLabel: 'SSH key',
    sshKeyHint: 'Installed as root access on first boot. Optional — without a key you add access from the dashboard after purchase.',
    sshKeyEmpty: 'You have no SSH keys saved yet. No problem — you can also set up access after purchase.',
    sshKeyAdd: 'Add SSH key',
    sshKeyAddNew: 'Add new',
    modePredefined: 'Predefined',
    modeCustom: 'Custom configuration',
    soldOut: 'Sold out',
    specs: {
      vcpu: '{n} vCPU',
      ram: '{n} GB RAM',
      disk: '{n} GB NVMe',
      snapshots: '{n} Snapshots',
      ipv4: '1× IPv4 + IPv6 /112'
    },
    customShape: {
      title: 'Custom configuration',
      subtitle: 'Freely combine vCPU, RAM and storage.',
      vcpu: 'vCPU',
      ram: 'RAM',
      disk: 'Storage',
      priceNote: 'Monthly: first month at order, then hourly, never more than the monthly price. Longer periods are paid in advance.',
      compare: 'Closest plan {name}: {price}',
      added: 'Custom configuration added to cart.'
    }
  },
  admin: {
    title: 'All VServers',
    subtitle: 'Manage VServers across all accounts',
    searchPlaceholder: 'Search by hostname or IP...',
    statusFilter: 'Status',
    statusAll: 'All statuses',
    table: {
      hostname: 'Hostname',
      account: 'Account',
      status: 'Status',
      ipv4: 'IPv4',
      plan: 'Plan',
      node: 'Node',
      createdAt: 'Created'
    },
    empty: {
      title: 'No VServers found',
      description: 'No VServers match the current filters.'
    },
    detail: {
      title: 'VServer details',
      forceProvision: 'Force provision',
      forceDeprovision: 'Force deprovision',
      backToList: 'Back to list',
      proxmoxInfo: 'Proxmox information',
      proxmoxVmid: 'VMID',
      proxmoxNode: 'Node',
      errorLabel: 'Last error',
      lockTitle: 'Lock',
      lockDescription: 'A lock stops the server and prevents start, reinstall, rescue, snapshot restore, console and firewall changes. Disk, IPs and billing stay, and the customer can still delete the server.',
      lockAction: 'Lock server',
      unlockAction: 'Unlock',
      lockedSince: 'Locked since {date}. The server stays stopped after unlocking.',
      lockDialogTitle: 'Lock server',
      lockDialogDescription: 'Server {hostname} is stopped and locked. The customer sees the reason in the dashboard and in the email.',
      lockNoteLabel: 'Reason for the customer',
      unlockConfirm: 'Unlock? The server stays stopped until the customer starts it.',
      lockSuccess: 'Server locked and stopped.',
      unlockSuccess: 'Lock lifted.'
    },
    smtp: {
      title: 'Port 25',
      subtitle: 'Early unblock requests and port 25 blocks',
      loadError: 'Could not load requests',
      actionError: 'Action failed',
      empty: 'No accounts in this view.',
      forced: 'Blocked by us',
      grandfathered: 'Existing customer',
      days: '{n} days',
      approveBlocked: 'Approval is possible once our block is lifted.',
      truncated: 'Only the first {n} accounts are shown, oldest requests first.',
      decisionNote: 'Reason for the decision: {note}',
      forcedReason: 'Reason for the block: {reason}',
      refusals: {
        grandfathered: 'Existing customers are not under the port 25 rule. Lock the VServer instead.',
        requestPending: 'A request is open. Decide it first, then block.',
        noPendingRequest: 'There is no open request any more. The list has been reloaded.',
        forced: 'Approval is possible once our block is lifted.',
        notBlocked: 'Port 25 is already open for this account. The list has been reloaded.'
      },
      tagSync: {
        deferred: 'Saved. The servers pick up the new state with the next sync, within 10 minutes.',
        failed: 'Saved, but the servers could not be updated. The next sync retries within 10 minutes.'
      },
      confirmFor: 'Account: {email}',
      noteLabel: 'Reason for the customer',
      notePlaceholder: 'The reason is emailed to the customer.',
      filter: {
        label: 'View',
        pending: 'Open requests',
        forced: 'Blocked by us',
        approved: 'Approved',
        denied: 'Denied',
        none: 'Without request',
        all: 'All'
      },
      statusLabel: {
        none: 'No request',
        pending: 'Open',
        approved: 'Approved',
        denied: 'Denied'
      },
      signals: {
        age: 'Account age',
        vservers: 'VServers',
        opensAt: 'Opens on',
        verified: 'Email verified',
        payment: 'Payment method',
        requested: 'Requested'
      },
      actions: {
        approve: 'Approve',
        approveWithoutRequest: 'Approve without request',
        deny: 'Deny',
        block: 'Block',
        unblock: 'Lift block',
        denyTitle: 'Deny request',
        blockTitle: 'Block port 25',
        approveSuccess: 'Port 25 approved',
        denySuccess: 'Request denied',
        blockSuccess: 'Port 25 blocked',
        unblockSuccess: 'Block lifted'
      }
    },
    ipPool: {
      title: 'IP pool',
      subtitle: 'Manage available IP addresses',
      available: 'Available',
      allocated: 'Allocated',
      reserved: 'Reserved',
      lowWarning: 'Few IPs available ({count}). Order more via Hetzner Robot.',
      addTitle: 'Register IP address',
      addressLabel: 'IP address',
      subnetLabel: 'Subnet (CIDR, optional)',
      gatewayLabel: 'Gateway (optional)',
      versionLabel: 'IP version',
      addAction: 'Add IP',
      addSuccess: 'IP added to the pool.',
      table: {
        address: 'Address',
        version: 'Version',
        status: 'Status',
        allocatedTo: 'Allocated to',
        allocatedAt: 'Allocated at'
      }
    },
    import: {
      title: 'Import VM',
      subtitle: 'Adopt an existing Proxmox VM as a managed VServer',
      accountTitle: 'Customer',
      accountMode: 'Account mode',
      existingAccount: 'Existing account',
      newAccount: 'Create new account',
      selectAccount: 'Select account',
      email: 'Email',
      name: 'First name',
      surname: 'Last name',
      org: 'Organisation',
      line1: 'Address',
      city: 'City',
      postcode: 'Postcode',
      country: 'Country (ISO)',
      state: 'State',
      phone: 'Phone',
      phoneCode: 'Dial code',
      stripeCustomer: 'Existing Stripe customer ID (optional)',
      cancelSub: 'Cancel old Stripe subscription (ID, optional)',
      cancelSubHint:
        'Enter the ID of an existing manual Stripe subscription to avoid double-billing.',
      vmTitle: 'VM & price',
      node: 'Proxmox node',
      nodeManualHint:
        'Node list unavailable. Enter the cluster node name (e.g. pve1), not the API address.',
      vmid: 'VMID',
      ipv4: 'IPv4 address',
      ipv6Block: 'IPv6 block /112 (optional)',
      ipv6BlockHint:
        "The routed /112, e.g. 2a01:4f8:191:8310::2:0/112. Without it the source filter blocks the server's IPv6.",
      subnet: 'Subnet (optional)',
      gateway: 'Gateway (optional)',
      hostname: 'Hostname (optional)',
      template: 'Template slug (optional)',
      priceEuro: 'Monthly price gross (€)',
      billingFreq: 'Billing cycle',
      freqMonthly: 'Monthly',
      freqAnnually: 'Annually',
      priceHint: 'Exact gross price incl. VAT. Billed from the 1st of next month.',
      vcpu: 'vCPU (optional)',
      memoryMb: 'RAM MB (optional)',
      diskGb: 'Disk GB (optional)',
      specsHint: 'Leave blank to read specs from the live VM.',
      submit: 'Import VM',
      success: 'VServer imported.',
      alreadyImported: 'Already imported.',
      invalidForm: 'Please fill in all required fields.',
      resultTitle: 'Result',
      rVserver: 'VServer',
      rSubscription: 'Subscription',
      rAccount: 'Account',
      rStripeSub: 'Stripe subscription',
      rTrialEnd: 'Billing starts',
      createdAccountNote: 'A new account was created. A welcome email was sent.',
      canceledNote: 'Old Stripe subscription {id} was cancelled.',
      otherSubsWarning:
        'Warning: the customer has other active Stripe subscriptions. Please reconcile manual billing:'
    },
    templates: {
      title: 'Templates',
      subtitle: 'Manage OS templates used for VServer provisioning',
      prereqHint:
        'The template must already exist as a cloud-init template VM in Proxmox — here you only register its VMID + node. (Not needed for importing existing VMs.)',
      addTitle: 'Register template',
      editTitle: 'Edit template',
      cancelEdit: 'Cancel',
      slug: 'Slug',
      name: 'Name',
      osFamily: 'OS family',
      osName: 'OS name',
      osVersion: 'OS version',
      vmid: 'Proxmox template VMID',
      node: 'Proxmox node',
      minDisk: 'Min disk (GB)',
      sortOrder: 'Sort order',
      active: 'Active',
      add: 'Add template',
      save: 'Save',
      edit: 'Edit',
      created: 'Template registered.',
      updated: 'Template updated.',
      empty: 'No templates registered yet.',
      activeYes: 'Active',
      activeNo: 'Inactive',
      table: {
        name: 'Name',
        slug: 'Slug',
        os: 'OS',
        vmid: 'VMID',
        node: 'Node',
        active: 'Status'
      }
    }
  }
} as const
